import { FC, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import {
  AD_LIBRARY_FILTER_TABS,
  InsightCategory,
  SmartInsightsAIRecommendations,
} from 'src/charts/SmartInsightsAIRecommendations'
import { useGetAdsLibrarySmartInsightsQuery } from 'src/reactQuery/useGetAdsLibrarySmartInsightsQuery'
import { useGetAdsLibrarySearchQuery } from 'src/reactQuery/useGetAdsLibrarySearchQuery'
import { isNumericPageId } from 'src/utils/isNumericPageId'
import { AboutTab } from './AboutTab'
import { ActiveAds } from './ActiveAds'
import { FiltersBar } from './FiltersBar'
import { Header } from './Header'
import { StatsCards } from './StatsCards'
import classes from './adLibraryDetails.module.scss'
import { AdLibraryBreadcrumb } from '../Breadcrumb'
import { CompetitorsComparison } from '../Main/CompetitorsComparison'
import { SavedCompetitors } from '../Main/SavedCompetitors'

export interface AdFilters {
  activeStatus: string
  language: string
  mediaType: string
  platform: string
}

export const AdLibraryDetails: FC = () => {
  const { pageId } = useParams<{ pageId: string }>()
  const location = useLocation()
  const navState = location.state as { pageName?: string } | null
  const [searchParams] = useSearchParams()
  const adType = searchParams.get('adType') || 'ALL'
  const isPolitical = adType === 'POLITICAL_AND_ISSUE_ADS'
  const [activeTab, setActiveTab] = useState<'ads' | 'about'>('ads')
  const [filters, setFilters] = useState<AdFilters>({
    activeStatus: 'Active ads',
    language: 'All languages',
    mediaType: 'All media types',
    platform: 'All platforms',
  })

  // Scoped to this advertiser page — without pageId the backend serves the
  // user's brand-wide insights instead.
  const { data: rawInsights, isLoading: smartInsightsLoading } =
    useGetAdsLibrarySmartInsightsQuery(pageId)

  const adActiveStatus =
    filters.activeStatus === 'Active ads'
      ? 'ACTIVE'
      : filters.activeStatus === 'Inactive ads'
        ? 'INACTIVE'
        : 'ALL'

  // The ads-search endpoint requires a numeric page id and 502s on a slug, so
  // only query when the URL pageId is numeric (it always is via in-app nav).
  const { data: adsData, isLoading: adsLoading } = useGetAdsLibrarySearchQuery({
    ad_active_status: adActiveStatus,
    ad_type: isPolitical ? 'POLITICAL_AND_ISSUE_ADS' : 'ALL',
    search_page_ids: pageId && isNumericPageId(pageId) ? pageId : '',
  })

  const smartInsightsData = rawInsights
    ? {
        hasInsights: rawInsights.hasInsights,
        insights: rawInsights.insights?.map((i) => ({
          ...i,
          category: i.category as InsightCategory,
        })),
      }
    : undefined

  // Prefer the advertiser's real name/id from the loaded ads. When the page has
  // no active ads, fall back to the name passed via navigation (from the search
  // result or competitor card), then to the URL pageId.
  const resolvedAd = adsData?.data?.[0]
  const savePageId = resolvedAd?.page_id || pageId || ''
  const pageName = resolvedAd?.page_name || navState?.pageName || pageId || ''

  return (
    <div className={classes.container}>
      <AdLibraryBreadcrumb currentPage={pageName} />
      <div className={classes.content}>
        <Header
          activeTab={activeTab}
          onTabChange={setActiveTab}
          pageName={pageName}
          savePageId={savePageId}
        />

        {activeTab === 'ads' ? (
          <>
            <StatsCards
              ads={adsData?.data}
              isPolitical={isPolitical}
              pageId={savePageId}
            />
            <FiltersBar filters={filters} onFiltersChange={setFilters} />
            <ActiveAds
              ads={adsData?.data}
              isLoading={adsLoading}
              isPolitical={isPolitical}
              pageId={savePageId}
              total={adsData?.data?.length ?? 0}
            />
            <SavedCompetitors />
            <CompetitorsComparison />
            <SmartInsightsAIRecommendations
              data={smartInsightsData}
              filterTabs={AD_LIBRARY_FILTER_TABS}
              homePageVariant
              isLoading={smartInsightsLoading}
            />
          </>
        ) : (
          <AboutTab pageId={savePageId} pageName={pageName} />
        )}
      </div>
    </div>
  )
}
